import { endOfDay, startOfDay } from 'date-fns'

import { Duration } from '../types/TrackingEvents'

type UseEventDialogDurationHookParams = {
  durationStart?: number
  durationEnd?: number
  durations?: Duration[]
}

const toStartOfDayMs = (ts: number) => startOfDay(new Date(ts)).getTime()
const toEndOfDayMs = (ts: number) => endOfDay(new Date(ts)).getTime()

export const useEventDialogDuration = ({ durationStart, durationEnd, durations = [] }: UseEventDialogDurationHookParams) => {
  const now = Date.now()

  const sortedDurations = [...durations].sort((a, b) => b.start - a.start)

  // Normalize to calendar day before comparing — raw API timestamps and locally-normalized
  // timestamps can differ by the timezone offset, causing exact ms matches to fail.
  const exactDuration =
    durationStart !== undefined && durationEnd !== undefined
      ? sortedDurations.find(
          (duration) => toStartOfDayMs(duration.start) === toStartOfDayMs(durationStart) && toEndOfDayMs(duration.end) === toEndOfDayMs(durationEnd)
        )
      : undefined

  const containingDuration =
    !exactDuration && durationStart !== undefined
      ? sortedDurations.find(
          (duration) => toStartOfDayMs(duration.start) <= toStartOfDayMs(durationStart) && toStartOfDayMs(duration.end) >= toStartOfDayMs(durationStart)
        )
      : undefined

  // If explicit params were given but not found in durations (e.g. old blueprint event not in API response),
  // use the params directly rather than falling through to an unrelated duration.
  const fallbackFromParams: Duration | undefined =
    !exactDuration && !containingDuration && durationStart !== undefined && durationEnd !== undefined
      ? ({ start: durationStart, end: durationEnd } as Duration)
      : undefined

  const durationsBeforeToday = sortedDurations.filter((duration) => duration.start < now)
  const duration = (exactDuration || containingDuration || fallbackFromParams || durationsBeforeToday?.[0] || sortedDurations?.[0]) as Duration | undefined

  return duration
}
